import React, { useState, useEffect } from "react";

import Localization from "../Localization";
import { formatAs } from "../settings.js";

const fields = [
  {
    field: "Capacity (MW)",
    label: "Capacity",
    format: (v) => `${formatAs.number(v)} MW`,
  },
  { field: "Project Status", label: "Status" },
  { field: "Funding Year" },
  { field: "M&A Year" },
  { field: "Year of Commission" },
  { field: "Technology" },
  { field: "State/Province" },
  {
    field: "Country",
    format: (v) =>
      Localization.getLanguage() === "zh"
        ? Localization.get(v.toUpperCase())
        : v,
  },
  {
    field: "Region",
    format: (v) =>
      Localization.getLanguage() === "zh"
        ? Localization.get(v.toUpperCase())
        : v,
  },
  {
    field: "Estimated Annual CO2 Emissions from Power Generation (1000 ton)",
    format: (v) =>
      `${
        isNaN(v) ? "NA" : `${formatAs.number(+v)} ${Localization.get("K Ton")}`
      }`,
  },
  { field: "FDI?", label: "FDI" },
  { field: "Investing Company" },
  { field: "Investing Company Ownership (%)", label: "Ownership %" },
  { field: "Deal Type", label: "" },
  // { field: "Involving Chinese DFI?", label: "DFI" },
  { field: "Lender" },
  { field: "Borrower", format: (v) => Localization.get(v) },
  { field: "Prior Owner" },
  {
    field: "Source",
    format: (value, project) => {
      const source1 = project["Source 1"];
      const source2 = project["Source 2"];
      if (!source1 && !source2) {
        return "n/a";
      }
      const sources = [source1, source2].filter((s) => s);
      const linkTags = sources.map((link, linkIndex) => {
        if (!link.toLowerCase().startsWith("http")) {
          return <span key={link}>{link} </span>;
        }
        return (
          <span key={link}>
            <a href={link} target="_blank" rel="noopener noreferrer">
              {Localization.get("Source")} {linkIndex + 1}
            </a>{" "}
          </span>
        );
      });
      return <span>{linkTags}</span>;
    },
  },
];

let scrollHandle;
function scrollToTarget(targetRef) {
  clearTimeout(scrollHandle);
  if (targetRef.current) {
    targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  } else {
    // in the (unlikely) case the table hasn't finished updating wait a bit and try again
    scrollHandle = setTimeout(() => scrollToTarget(targetRef), 600);
  }
}

export default function ProjectTooltip(props) {
  const { xOffset, yOffset, lockedProject, updateLockedProject } = props;
  const toRight = xOffset < 0;

  const [lockable, setLockable] = useState(false);
  useEffect(() => {
    setLockable((lockable) =>
      props.lockable !== lockable ? !lockable : lockable
    );
  }, [props.lockable]);

  const project = props.data.projects[0];
  const projectDetails = project.details;
  const unlock = lockedProject
    ? lockedProject.plantID === project.plantID
    : false;

  if (unlock && !props.lockedTooltip && !props.table) {
    return null;
  }
  let hasMultipleValues = false;
  const tBody = fields
    .filter((field) => {
      const value = projectDetails[field.field];
      return value !== undefined && value !== null && value !== "";
    })
    .map((field) => {
      const label = field.label || field.field;
      const displayLabel =
        label ===
        "Estimated Annual CO2 Emission from Power Generation (1000 ton)" ? (
          <span>
            CO<sub>2</sub> {Localization.get("Emissions")}
          </span>
        ) : (
          Localization.get(label)
        );
      let value = projectDetails[field.field];
      if (field.format) {
        value = field.format(value, projectDetails);
      } else {
        value = Localization.get(value, true);
      }
      const multiValueFields = [
        "Year of Commission",
        "Technology",
        "Project Status",
        "Lender",
        "Deal Type",
      ];
      let showAsterisk = false;
      if (multiValueFields.includes(field.field)) {
        if (value.includes(",")) {
          hasMultipleValues = true;
          showAsterisk = true;
        }
      }
      return (
        <tr key={field.field} style={field.style}>
          <td>{displayLabel}</td>
          <td>
            {value}
            {showAsterisk ? "*" : ""}
          </td>
        </tr>
      );
    });
  return (
    <div className={`tooltip projectTooltip ${unlock ? "locked" : ""}`}>
      <div className="tooltipTitle">{projectDetails["Plant Name"]}</div>
      <div className="content">
        <table>
          <tbody>{tBody}</tbody>
        </table>
        {hasMultipleValues && (
          <div className="asterisk">
            * {Localization.get("Multiple values")}
          </div>
        )}
        {unlock && !props.table ? (
          <div className="button toTable">
            <button
              type="button"
              onClick={() => scrollToTarget(props.lockedRowRef)}
            >
              {Localization.get("View in Table")}
            </button>
          </div>
        ) : null}
      </div>
      <div
        className={`lockIndicator ${toRight ? "right" : ""} ${
          lockable ? "active" : ""
        } ${unlock ? "locked" : ""}`}
        style={{ marginBottom: `${100 + yOffset}%` }}
        onClick={() => updateLockedProject(lockedProject, false, false)}
      >
        {unlock
          ? Localization.get("Click to Unlock Project")
          : Localization.get("Click to Lock Project")}
      </div>
    </div>
  );
}
